import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon } from 'lucide-react';
interface Volume {
  icon: string;
  number: string;
  title: string;
  subtitle: string;
  problem: string;
  bullets: string[];
  result: string;
  popular?: boolean;
}
const volumes: Volume[] = [{
  icon: '📗',
  number: 'Component 1 — 25 classes',
  title: 'Hip Hop Foundations, Grooves & Body Control',
  subtitle: 'The base your students need to move with confidence, rhythm, and real hip hop feel from day one',
  problem: 'Teaching hip hop fundamentals is one of the most rewarding — and most demanding — parts of being a dance instructor. This component gives you everything you need to guide new students with clarity, structure, and genuine hip hop identity, without improvising a single cue.',
  bullets: ['How to introduce hip hop posture, weight distribution, knee positioning, and body readiness from the very first session', 'How to teach the bounce, the rock, and lateral grooves — with full coordination breakdowns and musical connection', 'How to guide head, shoulder, chest, and hip isolations with clear verbal cues and corrections', 'How to sequence body rolls, weight shifts, four-direction steps, and basic arm patterns progressively and safely', 'How to build short combinations that integrate grooves, levels, directions, and arms — and guide students through memorizing them'],
  result: 'Your students feel the groove, understand the movement, and come back every week — from the very first class.',
  popular: true
}, {
  icon: '📘',
  number: 'Component 2 — 25 classes',
  title: 'Hip Hop Footwork, Steps & Skill Progressions',
  subtitle: 'Structured, progressive, and ready to teach at every step of the journey',
  problem: 'Footwork and classic hip hop steps are among the most requested — and most difficult to teach consistently week after week. This component gives you 25 fully structured sessions with varied technical focus, so every class feels intentional, progressive, and complete.',
  bullets: ['How to teach the Running Man, Roger Rabbit, Happy Feet, Heel-Toe, Criss Cross, Cabbage Patch, and more — with full step-by-step breakdowns and the most common error corrections', 'How to develop coordination between arms and legs, clean weight transfers, and gradual speed increases without losing technique', 'How to build footwork sequences that travel through space, change levels, and connect cleanly into combinations', 'How to teach transitions between steps so movement flows naturally without unnecessary pauses or resets', 'How to structure a progressive intermediate footwork combination with direction changes, dynamics, and musical timing'],
  result: 'You teach classic hip hop steps with confidence and consistency — and your students feel the clear progression every single week.'
}, {
  icon: '📙',
  number: 'Component 3 — 25 classes',
  title: 'Hip Hop Combinations, Freestyle & Choreography',
  subtitle: 'Movement with intention — fully guided from the first eight-count to the final phrase',
  problem: 'Teaching freestyle and choreography demands precise cues, musical awareness, and a clear sense of creative structure. This component removes the planning burden entirely and gives you sessions that flow beautifully from the first movement to the last.',
  bullets: ['How to teach musical structure — eight-count phrases, downbeats, upbeats, accents, and silences — and translate them into body movement', 'How to introduce freestyle with simple rules that reduce fear, build confidence, and keep every student engaged', 'How to guide freestyle by groove, direction, level, arms, footwork, and emotion — so every student finds their own voice', 'How to teach cypher dynamics, call and response, follow the leader, and partner exchanges with clear participation guidelines', 'How to guide students through building their own combinations — selecting steps, organizing phrases, developing transitions, and presenting their work'],
  result: 'Your freestyle and choreography classes feel creative, structured, and deeply satisfying for your students — every single time.'
}, {
  icon: '📕',
  number: 'Component 4 — 25 classes',
  title: 'Hip Hop Performance, Class Planning & Instructor System',
  subtitle: 'The sessions your performance-ready students need — and the ones that are hardest to structure well',
  problem: 'Performance-level teaching requires a completely different kind of preparation: longer sequences, artistic expression, group synchronization, and the entire showcase process. This component gives you everything structured so you can focus completely on your students.',
  bullets: ['How to develop stage presence, natural facial expression, movement projection, and energy in large spaces — with activities that build confidence without pressure', 'How to guide choreography cleaning — unifying arms, legs, directions, levels, and endings across the whole group', 'How to teach formations, spacing, entries and exits, canon effects, and contrast between group sections', 'How to structure rehearsals by sections, run full run-throughs, and correct efficiently without stopping the flow of the class', 'How to design 45, 60, and 75-minute classes and plan a complete training cycle from week one to showcase day'],
  result: 'Your students perform like artists — and you lead that process with a complete, professional structure.'
}];
export function TomosSection() {
  return <section className="py-24 px-4 bg-cream-dark border-y border-line" data-id="element-284" data-mp="y2rlw329igj9">
      <div className="max-w-6xl mx-auto" data-id="element-285" data-mp="1ulph97c33850">
        <div className="text-center mb-16" data-id="element-286" data-mp="1umqunigko6pw">
          <h2 className="text-4xl md:text-5xl font-serif font-bold uppercase mb-4 text-ink" data-id="element-287" data-mp="1pt4q6dkkb956">
            📚 The 6-in-1 System — Complete Breakdown
          </h2>
          <p className="text-xl text-burgundy font-semibold" data-id="element-288" data-mp="3p14nd1z90xok">
            4 Volumes · 100 complete classes · Nothing to build from scratch
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-289" data-mp="3abyesneitv40">
          {volumes.map((vol, index) => <motion.article key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          y: -5
        }} className={`bg-cream-light rounded-2xl border ${vol.popular ? 'border-burgundy shadow-[0_10px_30px_rgba(123,30,43,0.12)]' : 'border-line shadow-sm'} p-8 relative overflow-hidden flex flex-col h-full`} data-id="element-290" data-mp="3f9qfa5t8epjt" data-mpi={index}>
              {vol.popular && <div className="absolute top-6 right-[-35px] bg-burgundy text-cream-light font-bold text-xs py-1 px-10 rotate-45 shadow-lg" data-id="element-291" data-mp="ukaubrgp1v23">
                  MOST POPULAR
                </div>}

              <div className="flex items-center gap-4 mb-6" data-id="element-292" data-mp="2a1yu4ek45dwo">
                <span className="text-4xl" aria-hidden="true" data-id="element-293" data-mp="2otptmn3lpc07">
                  {vol.icon}
                </span>
                <div data-id="element-294" data-mp="2k0mzoduww0jh">
                  <div className="text-burgundy font-bold text-sm uppercase tracking-wider" data-id="element-295" data-mp="2k0eiwavo4b30">
                    {vol.number}
                  </div>
                  <h3 className="text-2xl font-serif font-bold uppercase leading-tight mt-1 text-ink" data-id="element-296" data-mp="2tqzfjsrs75ja">
                    {vol.title}
                  </h3>
                </div>
              </div>

              <p className="text-ink font-medium mb-6 italic border-l-4 border-gold pl-4" data-id="element-297" data-mp="32tqsxy47wbdk">
                {vol.subtitle}
              </p>

              <div className="mb-6" data-id="element-298" data-mp="1dfvhgh7wmopr">
                <h4 className="text-sm font-bold text-ink-muted uppercase tracking-wider mb-2" data-id="element-299" data-mp="18f7qfvjoe7vj">
                  What does it solve in your teaching?
                </h4>
                <p className="text-ink-soft text-sm leading-relaxed" data-id="element-300" data-mp="mjo7v3b5gnkb">
                  {vol.problem}
                </p>
              </div>

              <ul className="space-y-3 mb-8 flex-grow" data-id="element-301" data-mp="wb26l8va1jce">
                {vol.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-302" data-mp="119dgfjlkntum" data-mpi={i}>
                    <CheckCircle2Icon size={18} className="text-burgundy shrink-0 mt-0.5" data-id="element-303" data-mp="3mwiqd8xfe8c" />
                    <span className="text-ink-soft text-sm" data-id="element-304" data-mp="8lzndwkzvifs">{bullet}</span>
                  </li>)}
              </ul>

              <div className="bg-cream border border-gold/70 rounded-xl p-4 mt-auto" data-id="element-305" data-mp="ijx8ib7k4z46">
                <span className="text-burgundy font-bold block mb-1 uppercase text-sm tracking-wide" data-id="element-306" data-mp="ihkn1o2lc6md">
                  Result:
                </span>
                <p className="text-ink text-sm" data-id="element-307" data-mp="1ty10xcyhyf74">{vol.result}</p>
              </div>
            </motion.article>)}
        </div>
      </div>
    </section>;
}